import {
  Component,
  input,
  output,
  computed,
  signal,
  HostBinding,
  HostListener,
  ElementRef,
  inject,
  DestroyRef,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FocusMonitor, FocusOrigin } from '@angular/cdk/a11y';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export type M3ToggleButtonSize = 'xs' | 'sm' | 'default' | 'lg' | 'xl';
export type M3ToggleButtonShape = 'round' | 'square';
export type M3ToggleButtonVariant =
  'elevated' | 'filled' | 'tonal' | 'outlined' | 'standard';

@Component({
  selector: 'app-m3-toggle-button',
  standalone: true,
  imports: [CommonModule],
  template: `
    <button
      #buttonEl
      type="button"
      [class]="buttonClass()"
      [disabled]="disabled()"
      [attr.aria-label]="ariaLabel()"
      [attr.aria-describedby]="describedBy()"
      [attr.aria-pressed]="selected()"
      [attr.aria-expanded]="expanded()"
      (click)="handleClick($event)"
      (mousedown)="handleMouseDown()"
      (mouseup)="handleMouseUp()"
      (mouseleave)="handleMouseLeave()"
      (touchstart)="handleTouchStart()"
      (touchend)="handleTouchEnd()"
      (keydown)="handleKeyDown($event)"
      (keyup)="handleKeyUp($event)"
      (focus)="focused.emit()"
      (blur)="handleBlur()"
    >
      @if (iconLeft()) {
        <span
          class="material-symbols-outlined icon-left"
          [attr.aria-hidden]="true"
        >
          {{
            selected() && selectedIconLeft() ? selectedIconLeft() : iconLeft()
          }}
        </span>
      }

      @if (hasTextContent()) {
        <span class="button-text">
          <ng-content></ng-content>
        </span>
      }

      @if (iconRight()) {
        <span
          class="material-symbols-outlined icon-right"
          [attr.aria-hidden]="true"
        >
          {{
            selected() && selectedIconRight()
              ? selectedIconRight()
              : iconRight()
          }}
        </span>
      }
    </button>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host {
        display: inline-block;
      }

      :host(.enforce-touch-target) {
        min-width: 48px;
        min-height: 48px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      @keyframes press-ripple-spread {
        0% {
          transform: scale(0.6);
          opacity: 0;
        }
        100% {
          transform: scale(1);
          opacity: 0.1;
        }
      }

      @keyframes press-ripple-fade {
        0% {
          opacity: 0.1;
        }
        100% {
          opacity: 0;
        }
      }

      button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        border: none;
        cursor: pointer;
        transition:
          background-color var(--md-comp-button-motion-state-layer),
          color var(--md-comp-button-motion-state-layer),
          box-shadow var(--md-comp-button-motion-elevation),
          border-radius var(--md-comp-button-motion-shape-morph),
          transform var(--md-comp-button-motion-shape-morph);
        position: relative;
        font-family: inherit;
        font-weight: 500;
        font-size: 14px;
        line-height: 20px;
        letter-spacing: 0.1px;
        box-sizing: border-box;
        min-height: 40px;

        &::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: inherit;
          opacity: 0;
          pointer-events: none;
          transform: scale(1);
        }

        &:focus-visible,
        &.show-focus-ring {
          outline: none;
          box-shadow:
            0 0 0 2px var(--md-sys-color-surface),
            0 0 0 4px var(--md-sys-color-secondary);
        }

        &:focus:not(:focus-visible):not(.show-focus-ring) {
          outline: none;
        }

        // Size variants with shape morphing
        &.size-xs {
          --button-border-radius-resting: 16px;
          --button-border-radius-pressed: 8px;
          --icon-opsz: 20;
          min-height: 28px;
          padding: 0 12px;
          font-size: 12px;
          line-height: 16px;

          .material-symbols-outlined {
            font-size: 16px;
          }
        }

        &.size-sm {
          --button-border-radius-resting: 18px;
          --button-border-radius-pressed: 8px;
          --icon-opsz: 20;
          min-height: 32px;
          padding: 0 16px;
          font-size: 13px;
          line-height: 18px;

          .material-symbols-outlined {
            font-size: 18px;
          }
        }

        &.size-default {
          --button-border-radius-resting: 20px;
          --button-border-radius-pressed: 10px;
          --icon-opsz: 24;
          min-height: 40px;
          padding: 0 24px;
          font-size: 14px;
          line-height: 20px;

          .material-symbols-outlined {
            font-size: 20px;
          }
        }

        &.size-lg {
          --button-border-radius-resting: 24px;
          --button-border-radius-pressed: 12px;
          --icon-opsz: 24;
          min-height: 48px;
          padding: 0 32px;
          font-size: 16px;
          line-height: 24px;

          .material-symbols-outlined {
            font-size: 24px;
          }
        }

        &.size-xl {
          --button-border-radius-resting: 28px;
          --button-border-radius-pressed: 14px;
          --icon-opsz: 28;
          min-height: 56px;
          padding: 0 40px;
          font-size: 18px;
          line-height: 28px;

          .material-symbols-outlined {
            font-size: 28px;
          }
        }

        // Shape variants with selected state morphing and press animation
        &.shape-round {
          border-radius: var(--button-border-radius-resting);

          &.selected {
            border-radius: var(--md-comp-button-shape-square);
          }

          &.pressing:not(.selected) {
            border-radius: var(--button-border-radius-pressed);
            transform: scale(0.96);
          }

          &.pressing.selected {
            border-radius: calc(var(--md-comp-button-shape-square) * 0.6);
            transform: scale(0.96);
          }
        }

        &.shape-square {
          border-radius: var(--md-comp-button-shape-square);

          &.selected {
            border-radius: var(--button-border-radius-resting);
          }

          &.pressing:not(.selected) {
            border-radius: calc(var(--md-comp-button-shape-square) * 0.6);
            transform: scale(0.96);
          }

          &.pressing.selected {
            border-radius: var(--button-border-radius-pressed);
            transform: scale(0.96);
          }
        }

        // Variant styles - Elevated
        &.variant-elevated {
          // Unselected state
          background: var(--md-sys-color-surface-container);
          color: var(--md-sys-color-primary);
          box-shadow:
            0 1px 2px 0 rgba(0, 0, 0, 0.3),
            0 1px 3px 1px rgba(0, 0, 0, 0.15);

          &::before {
            background: var(--md-sys-color-primary);
          }

          &:hover:not(:disabled) {
            box-shadow:
              0 2px 6px 2px rgba(0, 0, 0, 0.15),
              0 1px 2px 0 rgba(0, 0, 0, 0.3);

            &::before {
              opacity: 0.08;
            }

            .material-symbols-outlined {
              font-variation-settings:
                'FILL' 0,
                'wght' 500,
                'GRAD' 0,
                'opsz' var(--icon-opsz);
            }
          }

          &.pressing:not(:disabled) {
            box-shadow:
              0 1px 2px 0 rgba(0, 0, 0, 0.3),
              0 1px 3px 1px rgba(0, 0, 0, 0.15);

            &::before {
              opacity: 0.1;
              animation: press-ripple-spread 150ms
                var(--md-sys-motion-easing-expressive) forwards;
            }

            .material-symbols-outlined {
              font-variation-settings:
                'FILL' 0,
                'wght' 300,
                'GRAD' 0,
                'opsz' var(--icon-opsz);
            }
          }

          &.press-release:not(:disabled) {
            &::before {
              animation: press-ripple-fade 200ms
                var(--md-sys-motion-easing-standard) forwards;
            }
          }

          &:focus-visible:not(:disabled) {
            &::before {
              opacity: 0.1;
            }
          }

          // Selected state
          &.selected {
            background: var(--md-sys-color-primary);
            color: var(--md-sys-color-on-primary);
            box-shadow:
              0 1px 2px 0 rgba(0, 0, 0, 0.3),
              0 1px 3px 1px rgba(0, 0, 0, 0.15);

            &::before {
              background: var(--md-sys-color-on-primary);
            }

            &:hover:not(:disabled) {
              box-shadow:
                0 2px 6px 2px rgba(0, 0, 0, 0.15),
                0 1px 2px 0 rgba(0, 0, 0, 0.3);

              &::before {
                opacity: 0.08;
              }

              .material-symbols-outlined {
                font-variation-settings:
                  'FILL' 1,
                  'wght' 500,
                  'GRAD' 0,
                  'opsz' var(--icon-opsz);
              }
            }

            &.pressing:not(:disabled) {
              box-shadow:
                0 1px 2px 0 rgba(0, 0, 0, 0.3),
                0 1px 3px 1px rgba(0, 0, 0, 0.15);

              &::before {
                opacity: 0.1;
                animation: press-ripple-spread 150ms
                  var(--md-sys-motion-easing-expressive) forwards;
              }

              .material-symbols-outlined {
                font-variation-settings:
                  'FILL' 1,
                  'wght' 300,
                  'GRAD' 0,
                  'opsz' var(--icon-opsz);
              }
            }

            &.press-release:not(:disabled) {
              &::before {
                animation: press-ripple-fade 200ms
                  var(--md-sys-motion-easing-standard) forwards;
              }
            }

            &:focus-visible:not(:disabled) {
              &::before {
                opacity: 0.1;
              }
            }
          }
        }

        // Variant styles - Filled
        &.variant-filled {
          // Unselected state
          background: var(--md-sys-color-surface-container);
          color: var(--md-sys-color-on-surface-variant);

          &::before {
            background: var(--md-sys-color-on-surface-variant);
          }

          &:hover:not(:disabled) {
            &::before {
              opacity: 0.08;
            }

            .material-symbols-outlined {
              font-variation-settings:
                'FILL' 0,
                'wght' 500,
                'GRAD' 0,
                'opsz' var(--icon-opsz);
            }
          }

          &.pressing:not(:disabled) {
            &::before {
              opacity: 0.1;
              animation: press-ripple-spread 150ms
                var(--md-sys-motion-easing-expressive) forwards;
            }

            .material-symbols-outlined {
              font-variation-settings:
                'FILL' 0,
                'wght' 300,
                'GRAD' 0,
                'opsz' var(--icon-opsz);
            }
          }

          &.press-release:not(:disabled) {
            &::before {
              animation: press-ripple-fade 200ms
                var(--md-sys-motion-easing-standard) forwards;
            }
          }

          &:focus-visible:not(:disabled) {
            &::before {
              opacity: 0.1;
            }
          }

          // Selected state
          &.selected {
            background: var(--md-sys-color-primary);
            color: var(--md-sys-color-on-primary);

            &::before {
              background: var(--md-sys-color-on-primary);
            }

            &:hover:not(:disabled) {
              box-shadow:
                0 1px 2px 0 rgba(0, 0, 0, 0.3),
                0 1px 3px 1px rgba(0, 0, 0, 0.15);

              &::before {
                opacity: 0.08;
              }

              .material-symbols-outlined {
                font-variation-settings:
                  'FILL' 1,
                  'wght' 500,
                  'GRAD' 0,
                  'opsz' var(--icon-opsz);
              }
            }

            &.pressing:not(:disabled) {
              box-shadow: none;

              &::before {
                opacity: 0.1;
                animation: press-ripple-spread 150ms
                  var(--md-sys-motion-easing-expressive) forwards;
              }

              .material-symbols-outlined {
                font-variation-settings:
                  'FILL' 1,
                  'wght' 300,
                  'GRAD' 0,
                  'opsz' var(--icon-opsz);
              }
            }

            &.press-release:not(:disabled) {
              &::before {
                animation: press-ripple-fade 200ms
                  var(--md-sys-motion-easing-standard) forwards;
              }
            }

            &:focus-visible:not(:disabled) {
              &::before {
                opacity: 0.1;
              }
            }
          }
        }

        // Variant styles - Tonal
        &.variant-tonal {
          // Unselected state
          background: var(--md-sys-color-surface-container);
          color: var(--md-sys-color-on-surface-variant);

          &::before {
            background: var(--md-sys-color-on-surface-variant);
          }

          &:hover:not(:disabled) {
            &::before {
              opacity: 0.08;
            }

            .material-symbols-outlined {
              font-variation-settings:
                'FILL' 0,
                'wght' 500,
                'GRAD' 0,
                'opsz' var(--icon-opsz);
            }
          }

          &.pressing:not(:disabled) {
            &::before {
              opacity: 0.1;
              animation: press-ripple-spread 150ms
                var(--md-sys-motion-easing-expressive) forwards;
            }

            .material-symbols-outlined {
              font-variation-settings:
                'FILL' 0,
                'wght' 300,
                'GRAD' 0,
                'opsz' var(--icon-opsz);
            }
          }

          &.press-release:not(:disabled) {
            &::before {
              animation: press-ripple-fade 200ms
                var(--md-sys-motion-easing-standard) forwards;
            }
          }

          &:focus-visible:not(:disabled) {
            &::before {
              opacity: 0.1;
            }
          }

          // Selected state
          &.selected {
            background: var(--md-sys-color-secondary-container);
            color: var(--md-sys-color-on-secondary-container);

            &::before {
              background: var(--md-sys-color-on-secondary-container);
            }

            &:hover:not(:disabled) {
              box-shadow:
                0 1px 2px 0 rgba(0, 0, 0, 0.3),
                0 1px 3px 1px rgba(0, 0, 0, 0.15);

              &::before {
                opacity: 0.08;
              }

              .material-symbols-outlined {
                font-variation-settings:
                  'FILL' 1,
                  'wght' 500,
                  'GRAD' 0,
                  'opsz' var(--icon-opsz);
              }
            }

            &.pressing:not(:disabled) {
              box-shadow: none;

              &::before {
                opacity: 0.1;
                animation: press-ripple-spread 150ms
                  var(--md-sys-motion-easing-expressive) forwards;
              }

              .material-symbols-outlined {
                font-variation-settings:
                  'FILL' 1,
                  'wght' 300,
                  'GRAD' 0,
                  'opsz' var(--icon-opsz);
              }
            }

            &.press-release:not(:disabled) {
              &::before {
                animation: press-ripple-fade 200ms
                  var(--md-sys-motion-easing-standard) forwards;
              }
            }

            &:focus-visible:not(:disabled) {
              &::before {
                opacity: 0.1;
              }
            }
          }
        }

        // Variant styles - Outlined
        &.variant-outlined {
          // Unselected state
          background: transparent;
          color: var(--md-sys-color-on-surface-variant);
          border: 1px solid var(--md-sys-color-outline-variant);

          &::before {
            background: var(--md-sys-color-on-surface-variant);
          }

          &:hover:not(:disabled) {
            &::before {
              opacity: 0.08;
            }

            .material-symbols-outlined {
              font-variation-settings:
                'FILL' 0,
                'wght' 500,
                'GRAD' 0,
                'opsz' var(--icon-opsz);
            }
          }

          &.pressing:not(:disabled) {
            &::before {
              opacity: 0.1;
              animation: press-ripple-spread 150ms
                var(--md-sys-motion-easing-expressive) forwards;
            }

            .material-symbols-outlined {
              font-variation-settings:
                'FILL' 0,
                'wght' 300,
                'GRAD' 0,
                'opsz' var(--icon-opsz);
            }
          }

          &.press-release:not(:disabled) {
            &::before {
              animation: press-ripple-fade 200ms
                var(--md-sys-motion-easing-standard) forwards;
            }
          }

          &:focus-visible:not(:disabled) {
            &::before {
              opacity: 0.1;
            }
          }

          // Selected state
          &.selected {
            background: var(--md-sys-color-on-surface);
            color: var(--md-sys-color-surface);
            border-color: var(--md-sys-color-on-surface);

            &::before {
              background: var(--md-sys-color-surface);
            }

            &:hover:not(:disabled) {
              &::before {
                opacity: 0.08;
              }

              .material-symbols-outlined {
                font-variation-settings:
                  'FILL' 1,
                  'wght' 500,
                  'GRAD' 0,
                  'opsz' var(--icon-opsz);
              }
            }

            &.pressing:not(:disabled) {
              &::before {
                opacity: 0.1;
                animation: press-ripple-spread 150ms
                  var(--md-sys-motion-easing-expressive) forwards;
              }

              .material-symbols-outlined {
                font-variation-settings:
                  'FILL' 1,
                  'wght' 300,
                  'GRAD' 0,
                  'opsz' var(--icon-opsz);
              }
            }

            &.press-release:not(:disabled) {
              &::before {
                animation: press-ripple-fade 200ms
                  var(--md-sys-motion-easing-standard) forwards;
              }
            }

            &:focus-visible:not(:disabled) {
              &::before {
                opacity: 0.1;
              }
            }
          }
        }

        // Variant styles - Standard
        &.variant-standard {
          // Unselected state
          background: transparent;
          color: var(--md-sys-color-on-surface-variant);

          &::before {
            background: var(--md-sys-color-on-surface-variant);
          }

          &:hover:not(:disabled) {
            &::before {
              opacity: 0.08;
            }

            .material-symbols-outlined {
              font-variation-settings:
                'FILL' 0,
                'wght' 500,
                'GRAD' 0,
                'opsz' var(--icon-opsz);
            }
          }

          &.pressing:not(:disabled) {
            &::before {
              opacity: 0.1;
              animation: press-ripple-spread 150ms
                var(--md-sys-motion-easing-expressive) forwards;
            }

            .material-symbols-outlined {
              font-variation-settings:
                'FILL' 0,
                'wght' 300,
                'GRAD' 0,
                'opsz' var(--icon-opsz);
            }
          }

          &.press-release:not(:disabled) {
            &::before {
              animation: press-ripple-fade 200ms
                var(--md-sys-motion-easing-standard) forwards;
            }
          }

          &:focus-visible:not(:disabled) {
            &::before {
              opacity: 0.1;
            }
          }

          // Selected state
          &.selected {
            background: transparent;
            color: var(--md-sys-color-primary);

            &::before {
              background: var(--md-sys-color-primary);
            }

            &:hover:not(:disabled) {
              &::before {
                opacity: 0.08;
              }

              .material-symbols-outlined {
                font-variation-settings:
                  'FILL' 1,
                  'wght' 500,
                  'GRAD' 0,
                  'opsz' var(--icon-opsz);
              }
            }

            &.pressing:not(:disabled) {
              &::before {
                opacity: 0.1;
                animation: press-ripple-spread 150ms
                  var(--md-sys-motion-easing-expressive) forwards;
              }

              .material-symbols-outlined {
                font-variation-settings:
                  'FILL' 1,
                  'wght' 300,
                  'GRAD' 0,
                  'opsz' var(--icon-opsz);
              }
            }

            &.press-release:not(:disabled) {
              &::before {
                animation: press-ripple-fade 200ms
                  var(--md-sys-motion-easing-standard) forwards;
              }
            }

            &:focus-visible:not(:disabled) {
              &::before {
                opacity: 0.1;
              }
            }
          }
        }

        // Disabled state
        &:disabled {
          cursor: not-allowed;

          &::before {
            display: none !important;
          }

          &.variant-elevated,
          &.variant-filled,
          &.variant-tonal {
            background: rgba(
              from var(--md-sys-color-on-surface) r g b / 0.1
            ) !important;
            color: rgba(
              from var(--md-sys-color-on-surface) r g b / 0.38
            ) !important;
            box-shadow: none !important;
          }

          &.variant-outlined {
            background: transparent !important;
            color: rgba(
              from var(--md-sys-color-on-surface) r g b / 0.38
            ) !important;
            border-color: rgba(
              from var(--md-sys-color-on-surface) r g b / 0.12
            ) !important;
          }

          &.variant-standard {
            background: transparent !important;
            color: rgba(
              from var(--md-sys-color-on-surface) r g b / 0.38
            ) !important;
          }
        }
      }

      .material-symbols-outlined {
        font-variation-settings:
          'FILL' 0,
          'wght' 400,
          'GRAD' 0,
          'opsz' var(--icon-opsz, 24);
        transition: font-variation-settings
          var(--md-sys-motion-expressive-effects-fast);
      }

      button.selected .material-symbols-outlined {
        font-variation-settings:
          'FILL' 1,
          'wght' 400,
          'GRAD' 0,
          'opsz' var(--icon-opsz, 24);
      }

      .button-text {
        flex: 1;
        text-align: center;
      }

      .icon-left {
        margin-right: -4px;
      }

      .icon-right {
        margin-left: -4px;
      }

      @media (prefers-reduced-motion: reduce) {
        button {
          transition: none;

          &::before {
            animation: none !important;
          }

          &.pressing {
            transform: none;
          }
        }

        .material-symbols-outlined {
          transition: none;
        }
      }
    `,
  ],
})
export class M3ToggleButtonComponent {
  private focusMonitor = inject(FocusMonitor);
  private elementRef = inject(ElementRef);
  private destroyRef = inject(DestroyRef);

  private buttonEl = viewChild<ElementRef<HTMLButtonElement>>('buttonEl');

  /**
   * Makes the host focusable so MatMenuTrigger can restore focus to it.
   * The HostListener below then delegates focus to the inner button.
   */
  @HostBinding('attr.tabindex')
  get hostTabIndex(): number {
    return -1;
  }

  /**
   * Delegates focus from host to inner button.
   * This is needed because MatMenuTrigger restores focus to its host element,
   * but the actual focusable element is the inner button.
   */
  @HostListener('focus')
  onHostFocus(): void {
    this.buttonEl()?.nativeElement?.focus();
  }

  variant = input<M3ToggleButtonVariant>('filled');
  size = input<M3ToggleButtonSize>('default');
  shape = input<M3ToggleButtonShape>('round');
  selected = input<boolean>(false);
  disabled = input<boolean>(false);
  expanded = input<boolean>(false);
  enforceMinTouchTarget = input<boolean>(true);

  iconLeft = input<string>();
  iconRight = input<string>();
  selectedIconLeft = input<string>();
  selectedIconRight = input<string>();

  ariaLabel = input<string>();
  describedBy = input<string>();

  clicked = output<MouseEvent>();
  toggled = output<boolean>();
  focused = output<void>();
  blurred = output<void>();

  private isPressing = signal(false);
  private isReleasing = signal(false);
  private pressAnimationActive = signal(false);
  private focusOrigin: FocusOrigin = null;
  private showFocusRing = signal(false);

  constructor() {
    this.focusMonitor
      .monitor(this.elementRef, true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((origin) => {
        this.focusOrigin = origin;
        this.showFocusRing.set(origin === 'keyboard' || origin === 'program');
      });
  }

  @HostBinding('class.enforce-touch-target')
  get shouldEnforceTouchTarget(): boolean {
    const size = this.size();
    return this.enforceMinTouchTarget() && (size === 'xs' || size === 'sm');
  }

  hasTextContent = computed(() => {
    return true;
  });

  buttonClass = computed(() => {
    const pressing = this.isPressing();
    const releasing = this.isReleasing();
    const showRing = this.showFocusRing();

    const classes = [
      'm3-toggle-button',
      `size-${this.size()}`,
      `shape-${this.shape()}`,
      `variant-${this.variant()}`,
    ];

    if (this.selected()) {
      classes.push('selected');
    }

    if (pressing) {
      classes.push('pressing');
    }

    if (releasing) {
      classes.push('press-release');
    }

    if (showRing) {
      classes.push('show-focus-ring');
    }

    return classes.join(' ');
  });

  handleClick(event: MouseEvent): void {
    this.clicked.emit(event);
    this.toggled.emit(!this.selected());
  }

  handleMouseDown(): void {
    if (!this.disabled()) {
      this.isPressing.set(true);
      this.pressAnimationActive.set(true);
    }
  }

  handleMouseUp(): void {
    this.isPressing.set(false);
    this.isReleasing.set(true);

    setTimeout(() => {
      this.isReleasing.set(false);
      this.pressAnimationActive.set(false);
    }, 350);
  }

  handleMouseLeave(): void {
    if (this.isPressing()) {
      this.isPressing.set(false);
      this.isReleasing.set(true);

      setTimeout(() => {
        this.isReleasing.set(false);
        this.pressAnimationActive.set(false);
      }, 350);
    }
  }

  handleTouchStart(): void {
    if (!this.disabled()) {
      this.isPressing.set(true);
      this.pressAnimationActive.set(true);
    }
  }

  handleTouchEnd(): void {
    this.isPressing.set(false);
    this.isReleasing.set(true);

    setTimeout(() => {
      this.isReleasing.set(false);
      this.pressAnimationActive.set(false);
    }, 350);
  }

  handleKeyDown(event: KeyboardEvent): void {
    if ((event.key === 'Enter' || event.key === ' ') && !event.repeat) {
      if (!this.disabled()) {
        this.isPressing.set(true);
        this.pressAnimationActive.set(true);
      }
    }
  }

  handleBlur(): void {
    // Enter can move focus away (e.g. opening a dialog), so keyup never reaches this button
    this.isPressing.set(false);
    this.pressAnimationActive.set(false);
    this.blurred.emit();
  }

  handleKeyUp(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      this.isPressing.set(false);
      this.isReleasing.set(true);

      setTimeout(() => {
        this.isReleasing.set(false);
        this.pressAnimationActive.set(false);
      }, 350);
    }
  }
}
